import { createSlice } from '@reduxjs/toolkit';
import {
  getMeasuresTypesAsync,
  getMeasuresUnitsAsync,
  getMeasuresGenericTypesAsync,
  getMeasuresStatusAsync,
  getPeripheralsTypesAsync,
  getPeripheralsMeasureTypesAsync,
  getDevicesTypesAsync,
  getRacesTypesAsync,
  getSexesTypesAsync,
  getAgendaTypesAsync,
  getResolutionsAsync,
  getPathologiesTypesAsync,
  getPatientPersonalDataTypesMaritalStatusAsync,
  getPatientPersonalDataTypesLanguageAsync,
  getPatientPersonalDataTypesEducationAsync,
  getPatientPersonalDataTypesEthnicityAsync,
  getPatientHealthInsuranceTypehealthCenterAsync,
  getRelationshipsTypesAsync,
  getServiceTypeAsync,
  getServiceReasonTypeAsync,
  getSettingsLanguagesAsync,
  getSettingsMeasurementUnitsAsync,
  getSettingsDateFormatAsync, getPeripheralsAsync,
  getServiceStatesAsync,
} from "./asyncActions";
import { getServiceStateReasonsAsync, getGroupResolutionsAsync } from 'features/groupDetail/asyncActions';
import { initialState } from './initialState';
import { normalizeDataServiceReasonType } from "./operators";
import { setCatalogCache, getCatalogCache } from 'utils/catalogCache';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Helper to split raw resolution items into archived / notArchived sub-arrays. */
const splitResolutions = (items: any[]) => {
  const archived = items
    .filter((item: any) => item.archived)
    .map((item: any) => ({ value: item?.id, code: item?.code, label: item?.label ?? item?.code, archived: item?.archived }));
  const notArchived = items
    .filter((item: any) => !item.archived)
    .map((item: any) => ({ value: item?.id, code: item?.code, label: item?.label ?? item?.code, archived: item?.archived }));
  return { archived, notArchived };
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const mastersSlice = createSlice({
  name: 'masters',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ── Measure types ──────────────────────────────────────────────────
      .addCase(getMeasuresTypesAsync.pending, (state) => {
        state.measuresTypes.status = 'loading';
      })
      .addCase(getMeasuresTypesAsync.fulfilled, (state, action) => {
        state.measuresTypes.status = 'idle';
        const items = action.payload?.items ?? [];
        state.measuresTypes.data = items;
        setCatalogCache('measuresTypes', items);
      })
      .addCase(getMeasuresTypesAsync.rejected, (state) => {
        const cached = getCatalogCache('measuresTypes');
        if (cached) {
          state.measuresTypes.status = 'idle';
          state.measuresTypes.data = cached;
        } else {
          state.measuresTypes.status = 'failed';
        }
      })

      // ── Measure units ─────────────────────────────────────────────────
      .addCase(getMeasuresUnitsAsync.pending, (state) => {
        state.measuresUnits.status = 'loading';
      })
      .addCase(getMeasuresUnitsAsync.fulfilled, (state, action) => {
        state.measuresUnits.status = 'idle';
        state.measuresUnits.data = action.payload?.items;
      })
      .addCase(getMeasuresUnitsAsync.rejected, (state) => {
        state.measuresUnits.status = 'failed';
      })

      // ── Generic measure types ─────────────────────────────────────────
      .addCase(getMeasuresGenericTypesAsync.pending, (state) => {
        state.measuresGenericTypes.status = 'loading';
      })
      .addCase(getMeasuresGenericTypesAsync.fulfilled, (state, action) => {
        state.measuresGenericTypes.status = 'idle';
        const items = action.payload?.items ?? [];
        state.measuresGenericTypes.data = items;
        setCatalogCache('measuresGenericTypes', items);
      })
      .addCase(getMeasuresGenericTypesAsync.rejected, (state) => {
        const cached = getCatalogCache('measuresGenericTypes');
        if (cached) {
          state.measuresGenericTypes.status = 'idle';
          state.measuresGenericTypes.data = cached;
        } else {
          state.measuresGenericTypes.status = 'failed';
        }
      })

      // ── Measure status ────────────────────────────────────────────────
      .addCase(getMeasuresStatusAsync.pending, (state) => {
        state.measuresStatus.status = 'loading';
      })
      .addCase(getMeasuresStatusAsync.fulfilled, (state, action) => {
        state.measuresStatus.status = 'idle';
        state.measuresStatus.data = action.payload?.items;
      })
      .addCase(getMeasuresStatusAsync.rejected, (state, action) => {
        state.measuresStatus.status = 'failed';
      })

      // ── Peripherals types ─────────────────────────────────────────────
      .addCase(getPeripheralsTypesAsync.pending, (state) => {
        state.peripheralsTypes.status = 'loading';
      })
      .addCase(getPeripheralsTypesAsync.fulfilled, (state, action) => {
        state.peripheralsTypes.status = 'idle';
        const items = action.payload?.items ?? [];
        state.peripheralsTypes.data = items;
        setCatalogCache('peripheralsTypes', items);
      })
      .addCase(getPeripheralsTypesAsync.rejected, (state) => {
        const cached = getCatalogCache('peripheralsTypes');
        if (cached) {
          state.peripheralsTypes.status = 'idle';
          state.peripheralsTypes.data = cached;
        } else {
          state.peripheralsTypes.status = 'failed';
        }
      })

      // ── Peripherals ───────────────────────────────────────────────────
      .addCase(getPeripheralsAsync.pending, (state) => {
        state.peripherals.status = 'loading';
      })
      .addCase(getPeripheralsAsync.fulfilled, (state, action) => {
        state.peripherals.status = 'idle';
        state.peripherals.data = action.payload?.items;
      })
      .addCase(getPeripheralsAsync.rejected, (state) => {
        state.peripherals.status = 'failed';
      })

      // ── Peripheral measure types ──────────────────────────────────────
      .addCase(getPeripheralsMeasureTypesAsync.pending, (state) => {
        state.peripheralsMeasureTypes.status = 'loading';
      })
      .addCase(getPeripheralsMeasureTypesAsync.fulfilled, (state, action) => {
        state.peripheralsMeasureTypes.status = 'idle';
        state.peripheralsMeasureTypes.data = action.payload?.items;
      })
      .addCase(getPeripheralsMeasureTypesAsync.rejected, (state) => {
        state.peripheralsMeasureTypes.status = 'failed';
      })

      // ── Device types ──────────────────────────────────────────────────
      .addCase(getDevicesTypesAsync.pending, (state) => {
        state.devicesTypes.status = 'loading';
      })
      .addCase(getDevicesTypesAsync.fulfilled, (state, action) => {
        state.devicesTypes.status = 'idle';
        state.devicesTypes.data = action.payload?.items;
      })
      .addCase(getDevicesTypesAsync.rejected, (state) => {
        state.devicesTypes.status = 'failed';
      })

      // ── Races ─────────────────────────────────────────────────────────
      .addCase(getRacesTypesAsync.pending, (state) => {
        state.racesTypes.status = 'loading';
      })
      .addCase(getRacesTypesAsync.fulfilled, (state, action) => {
        state.racesTypes.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.racesTypes.data = items;
        setCatalogCache('racesTypes', items);
      })
      .addCase(getRacesTypesAsync.rejected, (state) => {
        const cached = getCatalogCache('racesTypes');
        if (cached) {
          state.racesTypes.status = 'idle';
          state.racesTypes.data = cached;
        } else {
          state.racesTypes.status = 'failed';
        }
      })

      // ── Sexes ─────────────────────────────────────────────────────────
      .addCase(getSexesTypesAsync.pending, (state) => {
        state.sexesTypes.status = 'loading';
      })
      .addCase(getSexesTypesAsync.fulfilled, (state, action) => {
        state.sexesTypes.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.sexesTypes.data = items;
        setCatalogCache('sexesTypes', items);
      })
      .addCase(getSexesTypesAsync.rejected, (state) => {
        const cached = getCatalogCache('sexesTypes');
        if (cached) {
          state.sexesTypes.status = 'idle';
          state.sexesTypes.data = cached;
        } else {
          state.sexesTypes.status = 'failed';
        }
      })

      // ── Agenda types ──────────────────────────────────────────────────
      .addCase(getAgendaTypesAsync.pending, (state) => {
        state.agendaTypes.status = 'loading';
      })
      .addCase(getAgendaTypesAsync.fulfilled, (state, action) => {
        state.agendaTypes.status = 'idle';
        state.agendaTypes.data = action.payload?.data;
      })
      .addCase(getAgendaTypesAsync.rejected, (state) => {
        state.agendaTypes.status = 'failed';
      })

      // ── Resolutions ───────────────────────────────────────────────────
      .addCase(getResolutionsAsync.pending, (state) => {
        state.resolutions.status = 'loading';
      })
      .addCase(getResolutionsAsync.fulfilled, (state, action) => {
        state.resolutions.status = 'idle';
        const rawItems: any[] = action.payload?.item?.items ?? [];
        const { archived, notArchived } = splitResolutions(rawItems);
        state.resolutions.archived = { data: archived, total: archived?.length };
        state.resolutions.notArchived = { data: notArchived, total: notArchived?.length };
        // Cache the combined array; restore handler will re-split.
        setCatalogCache('resolutions', rawItems);
      })
      .addCase(getResolutionsAsync.rejected, (state) => {
        const cached = getCatalogCache('resolutions');
        if (cached) {
          state.resolutions.status = 'idle';
          const { archived, notArchived } = splitResolutions(cached);
          state.resolutions.archived = { data: archived, total: archived?.length };
          state.resolutions.notArchived = { data: notArchived, total: notArchived?.length };
        } else {
          state.resolutions.status = 'failed';
        }
      })

      // ── Pathologies ───────────────────────────────────────────────────
      .addCase(getPathologiesTypesAsync.fulfilled, (state, action) => {
        state.pathologiesTypes.status = 'idle';
        state.pathologiesTypes.data = action.payload?.item?.items;
      })

      // ── Marital status ────────────────────────────────────────────────
      .addCase(getPatientPersonalDataTypesMaritalStatusAsync.fulfilled, (state, action) => {
        state.maritalStatus.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.maritalStatus.data = items;
        setCatalogCache('maritalStatus', items);
      })
      .addCase(getPatientPersonalDataTypesMaritalStatusAsync.rejected, (state) => {
        const cached = getCatalogCache('maritalStatus');
        if (cached) {
          state.maritalStatus.status = 'idle';
          state.maritalStatus.data = cached;
        } else {
          state.maritalStatus.status = 'failed';
        }
      })

      // ── Language ──────────────────────────────────────────────────────
      .addCase(getPatientPersonalDataTypesLanguageAsync.fulfilled, (state, action) => {
        state.language.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.language.data = items;
        setCatalogCache('language', items);
      })
      .addCase(getPatientPersonalDataTypesLanguageAsync.rejected, (state) => {
        const cached = getCatalogCache('language');
        if (cached) {
          state.language.status = 'idle';
          state.language.data = cached;
        } else {
          state.language.status = 'failed';
        }
      })

      // ── Education ─────────────────────────────────────────────────────
      .addCase(getPatientPersonalDataTypesEducationAsync.fulfilled, (state, action) => {
        state.education.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.education.data = items;
        setCatalogCache('education', items);
      })
      .addCase(getPatientPersonalDataTypesEducationAsync.rejected, (state) => {
        const cached = getCatalogCache('education');
        if (cached) {
          state.education.status = 'idle';
          state.education.data = cached;
        } else {
          state.education.status = 'failed';
        }
      })

      // ── Ethnicity ─────────────────────────────────────────────────────
      .addCase(getPatientPersonalDataTypesEthnicityAsync.fulfilled, (state, action) => {
        state.ethnicity.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.ethnicity.data = items;
        setCatalogCache('ethnicity', items);
      })
      .addCase(getPatientPersonalDataTypesEthnicityAsync.rejected, (state) => {
        const cached = getCatalogCache('ethnicity');
        if (cached) {
          state.ethnicity.status = 'idle';
          state.ethnicity.data = cached;
        } else {
          state.ethnicity.status = 'failed';
        }
      })

      // ── Health center ─────────────────────────────────────────────────
      .addCase(getPatientHealthInsuranceTypehealthCenterAsync.fulfilled, (state, action) => {
        state.healthCenter.status = 'idle';
        state.healthCenter.data = action.payload?.item?.items;
      })

      // ── Relationships ─────────────────────────────────────────────────
      .addCase(getRelationshipsTypesAsync.fulfilled, (state, action) => {
        state.relationships.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.relationships.data = items;
        setCatalogCache('relationships', items);
      })
      .addCase(getRelationshipsTypesAsync.rejected, (state) => {
        const cached = getCatalogCache('relationships');
        if (cached) {
          state.relationships.status = 'idle';
          state.relationships.data = cached;
        } else {
          state.relationships.status = 'failed';
        }
      })

      // ── Service type ──────────────────────────────────────────────────
      .addCase(getServiceTypeAsync.fulfilled, (state, action) => {
        state.serviceType.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.serviceType.data = items;
        setCatalogCache('serviceType', items);
      })
      .addCase(getServiceTypeAsync.rejected, (state) => {
        const cached = getCatalogCache('serviceType');
        if (cached) {
          state.serviceType.status = 'idle';
          state.serviceType.data = cached;
        } else {
          state.serviceType.status = 'failed';
        }
      })

      // ── Service reason type ───────────────────────────────────────────
      .addCase(getServiceReasonTypeAsync.fulfilled, (state, action) => {
        state.serviceTypeReason.status = 'idle';
        const normalized = normalizeDataServiceReasonType(action.payload?.item?.items);
        state.serviceTypeReason.data = normalized;
        setCatalogCache('serviceTypeReason', normalized);
      })
      .addCase(getServiceReasonTypeAsync.rejected, (state) => {
        const cached = getCatalogCache('serviceTypeReason');
        if (cached) {
          state.serviceTypeReason.status = 'idle';
          state.serviceTypeReason.data = cached;
        } else {
          state.serviceTypeReason.status = 'failed';
        }
      })

      // Piggy-back: the group-detail thunk hits the same endpoint, so reuse its
      // payload here to keep the masters catalog in sync without an extra HTTP
      // call after every mutation.
      .addCase(getServiceStateReasonsAsync.fulfilled, (state, action) => {
        const items = action.payload?.item?.items;
        if (!items) return;
        const normalized = normalizeDataServiceReasonType(items);
        state.serviceTypeReason.status = 'idle';
        state.serviceTypeReason.data = normalized;
        setCatalogCache('serviceTypeReason', normalized);
      })

      // Piggy-back: the resolutions admin tab (group-detail thunk) hits the same
      // endpoint as the masters picker, so reuse its payload to keep the
      // alarm-resolution select in sync without an extra HTTP call after every
      // mutation.
      .addCase(getGroupResolutionsAsync.fulfilled, (state, action) => {
        const items = action.payload?.item?.items;
        if (!items) return;
        const { archived, notArchived } = splitResolutions(items);
        state.resolutions.status = 'idle';
        state.resolutions.archived = { data: archived, total: archived?.length };
        state.resolutions.notArchived = { data: notArchived, total: notArchived?.length };
        setCatalogCache('resolutions', items);
      })

      // ── Settings languages ────────────────────────────────────────────
      .addCase(getSettingsLanguagesAsync.fulfilled, (state, action) => {
        state.settingsLanguages.data = action.payload?.item?.items || [];
      })

      // ── Settings measurement units ────────────────────────────────────
      .addCase(getSettingsMeasurementUnitsAsync.fulfilled, (state, action) => {
        state.settingsMeasurementUnits.data = action.payload?.item?.items;
      })

      // ── Settings date format ──────────────────────────────────────────
      .addCase(getSettingsDateFormatAsync.fulfilled, (state, action) => {
        state.settingsDateFormat.data = action.payload?.item?.items;
      })

      // ── Service states ────────────────────────────────────────────────
      .addCase(getServiceStatesAsync.pending, (state) => {
        state.serviceStates.status = 'loading';
      })
      .addCase(getServiceStatesAsync.fulfilled, (state, action) => {
        state.serviceStates.status = 'idle';
        const items = action.payload?.item?.items ?? [];
        state.serviceStates.data = items;
        setCatalogCache('serviceStates', items);
      })
      .addCase(getServiceStatesAsync.rejected, (state) => {
        const cached = getCatalogCache('serviceStates');
        if (cached) {
          state.serviceStates.status = 'idle';
          state.serviceStates.data = cached;
        } else {
          state.serviceStates.status = 'failed';
        }
      });
  },
});

export default mastersSlice.reducer;
